import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { AppetiteType } from '@risksmart-app/domain/src/types/consts/index';
import type { InsertAppetiteMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

// Base fields shared by all appetite types
interface AppetiteBaseInput {
  ParentIds: string[];
  Statement?: string | null;
  EffectiveDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
}

export type InsertAppetiteTRPCInput = AppetiteBaseInput &
  (
    | {
        AppetiteType: typeof AppetiteType.Risk;
        LowerAppetite?: number | null;
        UpperAppetite?: number | null;
      }
    | {
        AppetiteType: typeof AppetiteType.Impact;
        ImpactAppetite: number;
        ImpactId: string;
      }
    | {
        AppetiteType: typeof AppetiteType.Likelihood;
        LikelihoodAppetite?: number | null;
      }
  );

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
}): InsertAppetiteMutation {
  return {
    insertChildAppetite: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
}

export const useInsertAppetiteTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.appetite.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.appetitesByRiskId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.activeAppetitesByParentId.queryKey(),
        });
        // COI-471 parity: Apollo twin evicts the `risk` field (appetites
        // surface on the linked risk).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.pathKey(),
        });
      },
    }),
  });

  return {
    insertAppetite: async (
      variables: InsertAppetiteTRPCInput
    ): Promise<InsertAppetiteMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
